// Tap-and-Play Scoring Engine — viewport-fit, mobile-landscape friendly,
// with a per-action history log that lets the user revert any single action.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Play, Pause, Plus, RotateCcw, Undo2, History, X } from "lucide-react";
import BasketballCourt from "./BasketballCourt";

// ---------- defaults & helpers ----------
const blankStats = () => ({
  points: 0, rebounds_off: 0, rebounds_def: 0,
  assists: 0, steals: 0, blocks: 0, turnovers: 0, fouls: 0,
  shots: [],
});

export function buildDefaultGame() {
  const mkRoster = (prefix, names) =>
    names.map((n, i) => ({ id: `${prefix}-${i}`, name: n, number: i + 4, stats: blankStats() }));
  return {
    quarter: 1, clock: 720, running: false,
    timeoutsA: 4, timeoutsB: 4, foulsA: 0, foulsB: 0,
    teamA: { name: "Court Kings", players: mkRoster("a", ["A. Rivera","M. Chen","D. Okafor","J. Park","T. Brooks"]) },
    teamB: { name: "Skyline Suns", players: mkRoster("b", ["L. Diaz","K. Nakamura","R. Singh","S. Bauer","E. Mwangi"]) },
    activePlayerId: "a-0",
    log: [], // newest first
  };
}

const STAT_LABELS = {
  rebounds_off: "OREB", rebounds_def: "DREB", assists: "AST",
  steals: "STL", blocks: "BLK", turnovers: "TO", fouls: "PF",
};

const fmtClock = (s) => {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const tsClock = (q, s) => `Q${q} ${fmtClock(s)}`;
const newId = () => Math.random().toString(36).slice(2, 10);

// ---------- subcomponents (kept outside main fn for lint + perf) ----------
function PlayerRow({ p, isActive, onSelect }) {
  return (
    <button
      type="button"
      data-testid={`player-row-${p.id}`}
      onClick={() => onSelect(p.id)}
      className={`w-full text-left px-2 py-1.5 border-l-4 transition-colors flex items-center gap-2 ${
        isActive ? "border-court bg-elevated" : "border-transparent hover:bg-elevated/60"
      }`}
    >
      <span className="font-mono text-[10px] w-5 text-zinc-500">#{p.number}</span>
      <span className="flex-1 truncate font-medium text-xs">{p.name}</span>
      <span className="font-display font-black text-base tracking-tight">{p.stats.points}</span>
    </button>
  );
}

function LogEntry({ entry, onRevert }) {
  return (
    <div className="px-2 py-1.5 border-b border-zinc-900 text-[11px] flex items-center gap-2 hover:bg-elevated/40 group">
      <span className="font-mono text-zinc-600 w-12 shrink-0">{entry.gameTime}</span>
      <span className={`font-bold w-5 text-center ${entry.side === "A" ? "text-volt" : "text-neon"}`}>{entry.side}</span>
      <span className="flex-1 truncate text-zinc-300">
        <span className="text-zinc-500">#{entry.playerNumber}</span> {entry.playerName} — <span className="text-white">{entry.label}</span>
      </span>
      <button
        data-testid={`log-revert-${entry.id}`}
        onClick={() => onRevert(entry.id)}
        className="opacity-0 group-hover:opacity-100 text-[10px] font-bold tracking-widest uppercase text-foul hover:text-white"
        aria-label="Revert this action"
      >Revert</button>
    </div>
  );
}

// ---------- main component ----------
export default function ScoringEngine({ storageKey = "hm_sandbox_game", initialGame }) {
  const [state, setState] = useState(() => {
    if (initialGame) {
      return initialGame;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (!parsed.log) parsed.log = [];
        return parsed;
      }
    } catch (err) {
      console.warn("[scoring] failed to load cached game, starting fresh", err);
    }
    return buildDefaultGame();
  });

  useEffect(() => {
    if (initialGame) {
      setState(initialGame);
    }
  }, [initialGame]);

  const [pendingShot, setPendingShot] = useState(null);
  const [floaters, setFloaters] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const floaterId = useRef(0);

  // ---- derived ----
  const allPlayers = useMemo(() => [
    ...state.teamA.players.map(p => ({ ...p, side: "A" })),
    ...state.teamB.players.map(p => ({ ...p, side: "B" })),
  ], [state]);
  const active = allPlayers.find(p => p.id === state.activePlayerId) || allPlayers[0];
  const scoreA = state.teamA.players.reduce((s, p) => s + p.stats.points, 0);
  const scoreB = state.teamB.players.reduce((s, p) => s + p.stats.points, 0);

  // ---- persist + clock ----
  useEffect(() => { localStorage.setItem(storageKey, JSON.stringify(state)); }, [state, storageKey]);

  useEffect(() => {
    if (!state.running) return undefined;
    const t = setInterval(() => {
      setState(s => {
        if (!s.running) return s;
        if (s.clock <= 0) return { ...s, running: false };
        return { ...s, clock: s.clock - 1 };
      });
    }, 1000);
    return () => clearInterval(t);
  }, [state.running]);

  // ---- core mutator: applies a delta and records a log entry ----
  const apply = useCallback((player, side, delta, label) => {
    setState(s => {
      const teamKey = side === "A" ? "teamA" : "teamB";
      const team = { ...s[teamKey] };
      team.players = team.players.map(p => {
        if (p.id !== player.id) return p;
        const stats = { ...p.stats };
        Object.entries(delta).forEach(([k, v]) => {
          if (k === "shot") stats.shots = [...stats.shots, v];
          else stats[k] = Math.max(0, (stats[k] || 0) + v);
        });
        return { ...p, stats };
      });
      const teamFoulKey = side === "A" ? "foulsA" : "foulsB";
      const fouls = s[teamFoulKey] + (delta.fouls || 0);
      const entry = {
        id: newId(),
        ts: Date.now(),
        gameTime: tsClock(s.quarter, s.clock),
        side,
        playerId: player.id,
        playerName: player.name,
        playerNumber: player.number,
        delta,
        label,
      };
      return { ...s, [teamKey]: team, [teamFoulKey]: fouls, log: [entry, ...s.log].slice(0, 200) };
    });
  }, []);

  // ---- public action helpers ----
  const flash = (text) => {
    const id = ++floaterId.current;
    setFloaters(f => [...f, { id, text }]);
    setTimeout(() => setFloaters(f => f.filter(x => x.id !== id)), 900);
  };

  const addPoints = (n) => {
    if (!active) return;
    apply(active, active.side, { points: n }, `Made ${n}PT`);
    flash(`+${n}`);
  };

  const addStat = (key) => {
    if (!active) return;
    apply(active, active.side, { [key]: 1 }, STAT_LABELS[key]);
  };

  const recordShot = (made) => {
    if (!pendingShot || !active) return;
    const pts = pendingShot.type === "3pt" ? 3 : 2;
    const shot = { ...pendingShot, made, ts: Date.now() };
    const delta = made ? { shot, points: pts } : { shot };
    apply(active, active.side, delta, `${made ? "Made" : "Missed"} ${pts}PT`);
    if (made) flash(`+${pts}`);
    setPendingShot(null);
  };

  const spendTimeout = (side) => {
    setState(s => {
      const k = side === "A" ? "timeoutsA" : "timeoutsB";
      if (s[k] <= 0) return s;
      const entry = {
        id: newId(), ts: Date.now(), gameTime: tsClock(s.quarter, s.clock),
        side, playerId: "-", playerName: "Team", playerNumber: "-",
        delta: { _timeout: 1 }, label: "Timeout",
      };
      return { ...s, [k]: s[k] - 1, log: [entry, ...s.log].slice(0, 200) };
    });
  };

  const revertEntry = (entryId) => {
    setState(s => {
      const entry = s.log.find(e => e.id === entryId);
      if (!entry) return s;
      const log = s.log.filter(e => e.id !== entryId);

      // timeout reversal
      if (entry.delta._timeout) {
        const k = entry.side === "A" ? "timeoutsA" : "timeoutsB";
        return { ...s, [k]: s[k] + 1, log };
      }

      const teamKey = entry.side === "A" ? "teamA" : "teamB";
      const team = { ...s[teamKey] };
      team.players = team.players.map(p => {
        if (p.id !== entry.playerId) return p;
        const stats = { ...p.stats };
        Object.entries(entry.delta).forEach(([k, v]) => {
          if (k === "shot") stats.shots = stats.shots.filter(sh => sh.ts !== v.ts);
          else stats[k] = Math.max(0, (stats[k] || 0) - v);
        });
        return { ...p, stats };
      });

      const teamFoulKey = entry.side === "A" ? "foulsA" : "foulsB";
      const fouls = Math.max(0, s[teamFoulKey] - (entry.delta.fouls || 0));
      return { ...s, [teamKey]: team, [teamFoulKey]: fouls, log };
    });
  };

  const undoLast = () => {
    if (state.log.length === 0) return;
    revertEntry(state.log[0].id);
  };

  const resetGame = () => {
    if (window.confirm("Reset the sandbox game? This clears all stats.")) {
      localStorage.removeItem(storageKey);
      setState(buildDefaultGame());
    }
  };

  // Only show shots for the currently selected player on the court.
  const activeShots = useMemo(() => {
    if (!active) return [];
    return active.stats.shots.map(sh => ({ ...sh, side: active.side }));
  }, [active]);

  const selectPlayer = (id) => setState(s => ({ ...s, activePlayerId: id }));

  // ---- render ----
  return (
    <div className="h-[calc(100vh-2.5rem)] bg-obsidian text-white flex flex-col overflow-hidden">
      {/* Scoreboard bar */}
      <div className="border-b border-white/10 bg-charcoal shrink-0">
        <div className="max-w-7xl mx-auto px-3 py-2 grid grid-cols-3 items-center gap-2">
          <div className="text-left">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 truncate">{state.teamA.name}</div>
            <div data-testid="score-team-a" className="font-display font-black text-3xl sm:text-4xl text-white leading-none">{scoreA}</div>
            <div className="text-[9px] tracking-widest uppercase text-zinc-500">Fouls {state.foulsA} · TO {state.timeoutsA}</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Quarter</div>
            <div className="flex items-center justify-center gap-2 leading-none">
              <span className="font-display font-black text-2xl">Q{state.quarter}</span>
              <span data-testid="game-clock" className="font-mono text-xl text-court">{fmtClock(state.clock)}</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-1">
              <button data-testid="clock-toggle-btn" onClick={() => setState(s => ({ ...s, running: !s.running }))} className="p-1.5 bg-elevated border border-zinc-800 hover:border-court">
                {state.running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button data-testid="quarter-next-btn" onClick={() => setState(s => ({ ...s, quarter: Math.min(4, s.quarter + 1), clock: 720, running: false }))} className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest border border-zinc-800 hover:border-court">Next Q</button>
              <button data-testid="undo-btn" onClick={undoLast} className="p-1.5 bg-elevated border border-zinc-800 hover:border-court" aria-label="Undo last"><Undo2 className="w-3.5 h-3.5" /></button>
              <button data-testid="log-toggle-btn" onClick={() => setLogOpen(o => !o)} className={`p-1.5 border ${logOpen ? "bg-court border-court" : "bg-elevated border-zinc-800 hover:border-court"}`} aria-label="Toggle log">
                <History className="w-3.5 h-3.5" />
              </button>
              <button data-testid="reset-btn" onClick={resetGame} className="p-1.5 bg-elevated border border-zinc-800 hover:border-foul" aria-label="Reset"><RotateCcw className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 truncate">{state.teamB.name}</div>
            <div data-testid="score-team-b" className="font-display font-black text-3xl sm:text-4xl text-white leading-none">{scoreB}</div>
            <div className="text-[9px] tracking-widest uppercase text-zinc-500">Fouls {state.foulsB} · TO {state.timeoutsB}</div>
          </div>
        </div>
      </div>

      {/* Body — flex row that fills viewport */}
      <div className="flex-1 min-h-0 grid grid-cols-[120px_1fr_120px] sm:grid-cols-[160px_1fr_160px] gap-2 p-2">
        {/* Team A roster */}
        <section className="bg-charcoal border border-zinc-800 flex flex-col min-h-0">
          <header className="px-2 py-1.5 border-b border-zinc-800 flex items-center justify-between gap-1">
            <h3 className="font-display font-black tracking-tight text-sm truncate">{state.teamA.name}</h3>
            <button data-testid="timeout-a-btn" onClick={() => spendTimeout("A")} className="text-[9px] font-bold tracking-widest uppercase text-volt hover:text-white">TO</button>
          </header>
          <div className="flex-1 overflow-y-auto divide-y divide-zinc-900">
            {state.teamA.players.map(p => (
              <PlayerRow key={p.id} p={p} isActive={p.id === state.activePlayerId} onSelect={selectPlayer} />
            ))}
          </div>
        </section>

        {/* Court + actions */}
        <section className="bg-charcoal border border-zinc-800 flex flex-col min-h-0">
          <header className="px-3 py-1.5 border-b border-zinc-800 flex items-center justify-between gap-2">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 shrink-0">Active</div>
            <div className="font-display font-black tracking-tight text-sm truncate" data-testid="active-player-name">
              {active ? `#${active.number} ${active.name}` : "—"}
              <span className="ml-2 text-[10px] text-court">[Team {active?.side}]</span>
            </div>
          </header>

          <div className="relative flex-1 min-h-0 p-2 overflow-hidden flex items-center justify-center">
            <div className="w-full max-w-2xl max-h-full">
              <BasketballCourt shots={activeShots} onShot={setPendingShot} />
            </div>
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              {floaters.map(f => (
                <div key={f.id} className="font-display font-black text-court text-5xl animate-float-up">{f.text}</div>
              ))}
            </div>
          </div>

          {pendingShot && (
            <div className="border-t border-zinc-800 p-2 flex items-center gap-2 bg-elevated shrink-0" data-testid="pending-shot-bar">
              <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 shrink-0">{pendingShot.type === "3pt" ? "3PT" : "2PT"}</span>
              <button data-testid="shot-made-btn" onClick={() => recordShot(true)} className="flex-1 h-10 bg-neon text-black font-display font-black tracking-wider uppercase text-sm active:scale-95">Made</button>
              <button data-testid="shot-missed-btn" onClick={() => recordShot(false)} className="flex-1 h-10 bg-foul text-white font-display font-black tracking-wider uppercase text-sm active:scale-95">Miss</button>
              <button data-testid="shot-cancel-btn" onClick={() => setPendingShot(null)} className="px-2 h-10 border border-zinc-800 text-zinc-400 hover:text-white text-xs"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}

          {/* Quick actions */}
          <div className="grid grid-cols-5 lg:grid-cols-10 gap-1 p-2 border-t border-zinc-800 shrink-0">
            <button data-testid="qa-ft" onClick={() => addPoints(1)} className="h-12 bg-elevated border border-zinc-800 hover:border-court active:scale-95 flex flex-col items-center justify-center">
              <span className="font-display font-black text-lg text-court leading-none">1</span>
              <span className="text-[9px] tracking-widest uppercase text-zinc-400">FT</span>
            </button>
            <button data-testid="qa-2pt" onClick={() => addPoints(2)} className="h-12 bg-elevated border border-zinc-800 hover:border-court active:scale-95 flex flex-col items-center justify-center">
              <span className="font-display font-black text-lg text-court leading-none">2</span>
              <span className="text-[9px] tracking-widest uppercase text-zinc-400">FG</span>
            </button>
            <button data-testid="qa-3pt" onClick={() => addPoints(3)} className="h-12 bg-elevated border border-zinc-800 hover:border-court active:scale-95 flex flex-col items-center justify-center">
              <span className="font-display font-black text-lg text-court leading-none">3</span>
              <span className="text-[9px] tracking-widest uppercase text-zinc-400">FG</span>
            </button>
            {Object.entries(STAT_LABELS).map(([key, label]) => (
              <button
                key={key}
                data-testid={`qa-${key}`}
                onClick={() => addStat(key)}
                className={`h-12 bg-elevated border border-zinc-800 active:scale-95 flex flex-col items-center justify-center ${key === "fouls" ? "hover:border-foul" : "hover:border-court"}`}
              >
                <Plus className="w-3 h-3 text-zinc-500" />
                <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-300">{label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Team B roster */}
        <section className="bg-charcoal border border-zinc-800 flex flex-col min-h-0">
          <header className="px-2 py-1.5 border-b border-zinc-800 flex items-center justify-between gap-1">
            <h3 className="font-display font-black tracking-tight text-sm truncate">{state.teamB.name}</h3>
            <button data-testid="timeout-b-btn" onClick={() => spendTimeout("B")} className="text-[9px] font-bold tracking-widest uppercase text-volt hover:text-white">TO</button>
          </header>
          <div className="flex-1 overflow-y-auto divide-y divide-zinc-900">
            {state.teamB.players.map(p => (
              <PlayerRow key={p.id} p={p} isActive={p.id === state.activePlayerId} onSelect={selectPlayer} />
            ))}
          </div>
        </section>
      </div>

      {/* Action log drawer (right side overlay) */}
      {logOpen && (
        <div className="absolute top-[7.5rem] right-2 bottom-2 w-80 bg-charcoal border border-court z-30 flex flex-col shadow-2xl" data-testid="action-log">
          <header className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <h4 className="font-display font-black tracking-tight text-sm uppercase">Play-by-Play</h4>
            <button onClick={() => setLogOpen(false)} className="text-zinc-400 hover:text-white" aria-label="Close log">
              <X className="w-4 h-4" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto">
            {state.log.length === 0 ? (
              <div className="p-4 text-xs text-zinc-500">No actions yet. Tap a stat to start recording.</div>
            ) : state.log.map(e => (
              <LogEntry key={e.id} entry={e} onRevert={revertEntry} />
            ))}
          </div>
          <div className="px-3 py-2 border-t border-zinc-800 text-[10px] tracking-widest uppercase text-zinc-500">
            Hover an entry → Revert to undo just that action.
          </div>
        </div>
      )}

      {/* Mobile portrait nudge */}
      <div className="md:hidden fixed inset-x-0 bottom-0 bg-court text-black text-center py-1 text-[10px] font-bold tracking-widest uppercase z-20 portrait:block landscape:hidden">
        Rotate device → for the full sideline view
      </div>
    </div>
  );
}
